/**
 * The contentPointer the editor's current content DESCENDS from.
 *
 * Written in exactly two places:
 *   1. on load, in `setupStore` — the pointer the content was fetched from
 *   2. after a successful promote — the pointer the backend returned
 *
 * It must NEVER be read from `story.settings.contentPointer` in Redux: that
 * value is precisely what goes stale when the store is re-seeded from an older
 * page record, so deriving the parent from it would make the compare-and-swap
 * vacuous. Module-level rather than React state because the writer
 * (`setupStore`) and the reader (`use-update-preview-only`) sit in different
 * trees.
 *
 * Keyed by scope (storyId + versionId): the editor session is page-scoped,
 * but a version-scoped save is compared server-side against
 * `version.contentPointer` — a different lineage from the page's. A single
 * global pointer let a version save get compared against the page's parent
 * (and vice versa), false-parking every republish of a live story. Every
 * export here therefore takes an explicit scope.
 */
export type PointerScope = {
  storyId: string;
  versionId?: string | null;
};

const scopeKey = ({ storyId, versionId }: PointerScope): string =>
  `${storyId}:${versionId ?? ""}`;

const lastKnownPointers = new Map<string, string | null>();

export const setLastKnownPointer = (
  scope: PointerScope,
  pointer: string | null,
): void => {
  lastKnownPointers.set(scopeKey(scope), pointer || null);
};

export const getLastKnownPointer = (scope: PointerScope): string | null =>
  lastKnownPointers.get(scopeKey(scope)) ?? null;

/** Give up reconciling after this many consecutive parks and surface an error. */
export const PARK_RECONCILE_LIMIT = 3;

// In-memory fallback for the two counters below, used only when sessionStorage
// throws (private mode / storage disabled). Keeps counting sane for the rest
// of this in-memory session even though it can't survive a real reload.
const parkCountFallback = new Map<string, number>();
const parkCapReportedFallback = new Map<string, boolean>();

const PARK_COUNT_PREFIX = "sd-parked-reconcile-count:";
const PARK_CAP_REPORTED_PREFIX = "sd-parked-cap-reported:";

/**
 * Consecutive parked autosaves for this scope. Persisted in sessionStorage —
 * NOT module memory — because the non-force park path calls
 * `window.location.reload()`, which wipes module memory but leaves
 * sessionStorage intact. A counter that resets to 0 on every reload can never
 * reach PARK_RECONCILE_LIMIT, turning "give up after N parks" into an
 * infinite park -> reload -> park loop.
 */
export const noteParkedReconcile = (scope: PointerScope): number => {
  const key = PARK_COUNT_PREFIX + scopeKey(scope);
  try {
    const current = Number(sessionStorage.getItem(key)) || 0;
    const next = current + 1;
    sessionStorage.setItem(key, String(next));
    return next;
  } catch {
    // Private mode / storage disabled — the cap can't survive a reload here
    // either way, but keep counting for this in-memory session.
    const next = (parkCountFallback.get(key) ?? 0) + 1;
    parkCountFallback.set(key, next);
    return next;
  }
};

/** Reset on any successful promote (including an adopted own-revision/converged
 * escape hatch), and on a fresh load of this scope's content. */
export const resetParkedReconcile = (scope: PointerScope): void => {
  const countKey = PARK_COUNT_PREFIX + scopeKey(scope);
  try {
    sessionStorage.removeItem(countKey);
  } catch {
    // ignore — see noteParkedReconcile
  }
  parkCountFallback.delete(countKey);

  const capKey = PARK_CAP_REPORTED_PREFIX + scopeKey(scope);
  try {
    sessionStorage.removeItem(capKey);
  } catch {
    // ignore
  }
  parkCapReportedFallback.delete(capKey);
};

/**
 * Whether the "parked repeatedly" cap has already been reported (Sentry +
 * toast) for this scope. Once tripped, further parks in the same run must NOT
 * re-fire the error toast / Sentry event on every autosave cycle — without
 * this a persistent park would toast and capture roughly every autosave
 * interval for as long as the user keeps typing.
 */
export const hasReportedParkCap = (scope: PointerScope): boolean => {
  const key = PARK_CAP_REPORTED_PREFIX + scopeKey(scope);
  try {
    return sessionStorage.getItem(key) === "1";
  } catch {
    return parkCapReportedFallback.get(key) ?? false;
  }
};

export const markParkCapReported = (scope: PointerScope): void => {
  const key = PARK_CAP_REPORTED_PREFIX + scopeKey(scope);
  try {
    sessionStorage.setItem(key, "1");
  } catch {
    parkCapReportedFallback.set(key, true);
  }
};

const PARKED_NOTICE_KEY = "sd-parked-reload-notice";

/**
 * A parked autosave triggers a reload, and a reload destroys any toast we show
 * beforehand — the user would just see the page refresh for no reason. Stash a
 * one-shot flag instead and surface the message after the reload, from
 * `setupStore`.
 */
export const setParkedReloadNotice = (): void => {
  try {
    sessionStorage.setItem(PARKED_NOTICE_KEY, "1");
  } catch {
    // Private mode / storage disabled — the reload still happens, we just
    // cannot explain it. Never block the reload on telemetry.
  }
};

export const consumeParkedReloadNotice = (): boolean => {
  try {
    if (sessionStorage.getItem(PARKED_NOTICE_KEY) !== "1") return false;
    sessionStorage.removeItem(PARKED_NOTICE_KEY);
    return true;
  } catch {
    return false;
  }
};

/**
 * Serializes autosave generations for one scope.
 *
 * A generation reads the parent pointer, spends seconds uploading a snapshot,
 * then promotes — and the read and the promote sit at opposite ends of that
 * gap. Two generations overlapping in one tab therefore both read the SAME
 * parent, the first promotes, and the second arrives with a parent the backend
 * has already moved past. Its compare-and-swap parks it, and the park path
 * reads that as a co-editor conflict and reloads the tab.
 *
 * That is not hypothetical: on 2026-08-28 story 6a6c576458b353916a9e4f19
 * logged `revision PARKED` with head, parent and incoming all carrying the
 * same sessionId and userId — one tab, one user, no co-editor.
 *
 * The debounce in use-autosave cannot close this: it only spaces out generation
 * STARTS, and publish (use-publish) calls the same hook directly with
 * `force: true`, outside the debounce entirely. Nor can a `useRef` guard —
 * use-autosave and use-publish each call `useUpdatePreviewOnly()`, so they hold
 * SEPARATE hook instances with no shared state. The queue therefore lives here,
 * at module level, next to the pointer it protects.
 *
 * Per scope, not global: a page save and a version save are independent
 * lineages, and two different stories must never wait on each other.
 */
const generationQueues = new Map<string, Promise<unknown>>();

/**
 * How long a queued generation will wait for the one ahead of it before giving
 * up and running anyway.
 *
 * Without this ceiling a single hung request (a fetch the browser never
 * settles) would wedge the queue for the rest of the session — and, because
 * publish now shares the queue, would block publishing too. That trades a rare
 * self-race for a permanent stall, which is the worse failure. A generation is
 * preview render + two uploads + record + persist; the production traces run
 * ~1.8s, so 30s means this only fires when something is genuinely stuck.
 */
export const QUEUE_WAIT_LIMIT_MS = 30_000;

// `previous` is always one of the never-rejecting `settled` links below (or the
// initial Promise.resolve()), so a plain `.then` cannot skip the wait.
const waitForTurn = (previous: Promise<unknown>): Promise<void> =>
  new Promise<void>((resolve) => {
    const timer = setTimeout(resolve, QUEUE_WAIT_LIMIT_MS);
    void previous.then(() => {
      clearTimeout(timer);
      resolve();
    });
  });

export const runExclusive = <T>(
  scope: PointerScope,
  task: () => Promise<T>,
): Promise<T> => {
  const key = scopeKey(scope);
  const previous = generationQueues.get(key) ?? Promise.resolve();
  const run = waitForTurn(previous).then(task);
  // The queue link swallows the outcome so it never rejects (an unhandled
  // rejection here would be reported against a caller that already handled it)
  // and so a failed generation cannot wedge the queue; `run` itself still
  // rejects for the real caller.
  const settled = run.then(
    () => undefined,
    () => undefined,
  );
  generationQueues.set(key, settled);
  void settled.then(() => {
    // Drop the entry once this is the last generation, so the map doesn't
    // retain one promise per story visited this session.
    if (generationQueues.get(key) === settled) generationQueues.delete(key);
  });
  return run;
};

/**
 * The pointers THIS browsing context has uploaded, per scope.
 *
 * Used to tell "I raced myself" apart from "a co-editor moved the head". The
 * reverted `own write` heuristic tried to do that by matching the head's
 * sessionId against meta.sessionId, but sessionId lives in a cookie shared by
 * every tab in the browser profile, so a stale tab could adopt a NEWER tab's
 * head as its own — exactly the re-seeding incident the park guard exists to
 * prevent. A registry of what this JS context actually uploaded has no such
 * hole: another tab has its own module state and can never contain this tab's
 * uploads, and a reload clears it (correctly — after a reload nothing in flight
 * is ours any more).
 *
 * Bounded: a long editing session uploads a snapshot every few seconds, and we
 * only ever need to recognise a head from the last few generations.
 */
export const OWN_POINTER_LIMIT = 20;

const ownPointers = new Map<string, string[]>();

export const noteOwnPointer = (scope: PointerScope, url: string): void => {
  if (!url) return;
  const key = scopeKey(scope);
  const seen = ownPointers.get(key) ?? [];
  seen.push(url);
  if (seen.length > OWN_POINTER_LIMIT)
    seen.splice(0, seen.length - OWN_POINTER_LIMIT);
  ownPointers.set(key, seen);
};

export const isOwnPointer = (scope: PointerScope, url: string): boolean => {
  if (!url) return false;
  return (ownPointers.get(scopeKey(scope)) ?? []).includes(url);
};

// ───── Present co-editors ────────────────────────────────────────────────────
//
// User ids currently in the Liveblocks room, published by YjsStoryProvider from
// room presence. A park can then tell "a LIVE co-editor holds the head" from
// "this tab is alone and stale". In the first case our edits already sit in the
// shared Y.Doc the co-editor autosaves from, so the right move is to adopt
// their head as our parent and carry on — reloading (the second case's
// remedy) re-seeds the tab from the durable head and drops everything typed
// since it, which is how Winter Circle lost the same edits five times over
// (Monday #13070251938). Module-level for the same reason as lastKnownPointers:
// the writer (the provider) and the reader (use-update-preview-only) sit in
// different trees.
// ponytail: one room per JS context; key by scope if the editor ever mounts two.
let presentPeerIds = new Set<string>();

// A Storydoc admin viewing another org's deck is `storydoc:<id>` in Liveblocks
// presence (collaboration/service.js handleAuth) but may be the plain `<id>` in
// the pointer path, or the reverse. Compare the bare id on both sides.
const ADMIN_ID_PREFIX = "storydoc:";
const bareUserId = (id: string): string =>
  id.startsWith(ADMIN_ID_PREFIX) ? id.slice(ADMIN_ID_PREFIX.length) : id;

export const setPresentPeerIds = (
  ids: readonly (string | null | undefined)[],
): void => {
  presentPeerIds = new Set(
    ids.filter((id): id is string => Boolean(id)).map(bareUserId),
  );
};

export const isPeerPresent = (userId: string | null | undefined): boolean =>
  Boolean(userId) && presentPeerIds.has(bareUserId(userId as string));

/**
 * The user id that minted a revision pointer. Pointers end in
 * `…/<sessionId>/<userId>:<epochMs>` (use-upload-metadata.tsx); internal admins
 * carry a `storydoc:<id>` userId, so split on the LAST colon, never the first.
 */
export const revisionWriterId = (
  url: string | null | undefined,
): string | null => {
  if (!url) return null;
  const lastSegment = url.split("/").pop() ?? "";
  const lastColon = lastSegment.lastIndexOf(":");
  return lastColon > 0 ? lastSegment.slice(0, lastColon) : null;
};

// ───── Live-sync probe ────────────────────────────────────────────────────────
//
// Whether this tab's Yjs transport is connected AND has no local updates the
// server has not acknowledged (YjsTransport.isConverged). Registered by
// YjsStoryProvider next to its transport and cleared when it is destroyed.
// Adopting a present co-editor's head is only safe when our local doc really is
// the shared doc: a tab whose socket silently fell behind would otherwise
// promote its lagging copy over the peer's edits on the next autosave — the
// same silent-lag condition as the re-entry stale-seed incident. No probe
// registered → not converged, so the park falls back to the reload path.
let liveSyncProbe: (() => boolean) | null = null;

export const setLiveSyncProbe = (probe: (() => boolean) | null): void => {
  liveSyncProbe = probe;
};

export const isLiveSyncConverged = (): boolean => {
  try {
    return liveSyncProbe?.() ?? false;
  } catch {
    return false;
  }
};

/**
 * Converged is momentarily false while the user is typing (own updates await
 * the server's ack), which is exactly when co-editor parks are most common —
 * so give the ack a short window instead of deciding on one instant. Resolves
 * true as soon as the probe says converged, false when `timeoutMs` elapses.
 */
export const LIVE_SYNC_WAIT_MS = 2_000;

export const awaitLiveSyncConverged = async (
  timeoutMs = LIVE_SYNC_WAIT_MS,
  pollMs = 100,
): Promise<boolean> => {
  const deadline = Date.now() + timeoutMs;
  while (!isLiveSyncConverged()) {
    if (Date.now() >= deadline) return false;
    await new Promise((resolve) => setTimeout(resolve, pollMs));
  }
  return true;
};
